import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { playerAPI, challengeAPI, ladderAPI } from '../lib/api';
import { ArrowLeft, Lightning, Trophy, TrendUp, CalendarBlank, Pause, PencilSimple, Camera, Check, X, Phone, ChatCircleText, EnvelopeSimple, SignOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Avatar from '../components/Avatar';
import RankMovement from '../components/RankMovement';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusPill from '../components/StatusPill';
import MemberLink from '../components/MemberLink';
import { isWalkover, formatScore } from '../lib/scoreFormat';
import { canChallengeTarget } from '../lib/challengeEligibility';

const USTA_RATINGS = ['3.0', '3.5', '4.0', '4.5', '5.0'];

export default function PlayerPage() {
  const { playerId } = useParams();
  const location = useLocation();
  const [playerData, setPlayerData] = useState(null);
  const [ladderRows, setLadderRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { player: myPlayer, user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  // Auto-open edit mode when the caller passes ?edit=1 (e.g. Settings pencil
  // icon or a direct link). Only honored when this is the viewer's own
  // profile — the server's `can_edit` flag is the source of truth so this
  // also works for admins and non-ladder members whose URL param is a
  // profiles.id rather than a players.id.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('edit') !== '1') return;
    const isSelfByPlayer = myPlayer?.player_id && myPlayer.player_id === playerId;
    const isSelfByProfile = user?.id && user.id === playerId;
    const isSelfByServer = playerData?.can_edit === true;
    if (isSelfByPlayer || isSelfByProfile || isSelfByServer) {
      setEditing(true);
    }
  }, [location.search, myPlayer, user, playerData, playerId]);

  const fetchPlayer = useCallback(async () => {
    try {
      // Fetch player profile AND the live ladder in parallel. The ladder rows
      // carry the viewer's outgoing/incoming challenge state AND the target's
      // active_challenge marker — both required for the canChallengeTarget()
      // helper to match the Ladder page's eligibility decisions exactly.
      const [playerRes, ladderRes] = await Promise.all([
        playerAPI.get(playerId),
        ladderAPI.getAll().catch(() => ({ data: { players: [] } })),
      ]);
      setPlayerData(playerRes.data);
      setLadderRows(ladderRes.data.players || []);
    } catch (err) {
      toast.error('Player not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [playerId, navigate]);

  useEffect(() => { fetchPlayer(); }, [fetchPlayer]);

  if (loading || !playerData) {
    // Render a lightweight skeleton of the profile shell so tapping in from
    // the ladder never shows a blank screen.
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="player-profile-loading">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-[#015e3f]"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-5 space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#e3dcd1] animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-5 w-2/3 bg-[#e3dcd1] rounded-md animate-pulse" />
              <div className="h-3 w-1/3 bg-[#e3dcd1] rounded-md animate-pulse" />
            </div>
          </div>
        </div>
        <div className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-5 space-y-2">
          <div className="h-3 w-1/2 bg-[#e3dcd1] rounded-md animate-pulse" />
          <div className="h-3 w-2/3 bg-[#e3dcd1] rounded-md animate-pulse" />
          <div className="h-3 w-1/3 bg-[#e3dcd1] rounded-md animate-pulse" />
        </div>
      </div>
    );
  }

  const { player, matches, challenges } = playerData;
  // Prefer the server's `can_edit` flag (Phase C1.5b) — works even for
  // members without a ladder row where `myPlayer?.player_id` would be null.
  const isMe = playerData?.can_edit === true;

  // Use the live ladder rows for the canonical viewer + target shapes. This
  // matches what LadderPage uses for its eligibility decisions, so the two
  // surfaces can never disagree. Falls back to inline reconstruction when
  // ladder data isn't available (e.g. fetch failed), in which case we still
  // refuse the Challenge button — better to under-show than over-show.
  const viewerLadderRow = ladderRows.find(r => r.player_id === myPlayer?.player_id) || null;
  const targetLadderRow = ladderRows.find(r => r.player_id === player.player_id) || {
    player_id: player.player_id,
    rank: player.rank,
    status: player.status,
    outgoing_challenge: null,
    incoming_challenge: null,
  };
  const canChallenge = !isMe && canChallengeTarget(viewerLadderRow, targetLadderRow, ladderRows);

  const totalMatches = player.wins + player.losses;
  const winRate = totalMatches > 0 ? Math.round((player.wins / totalMatches) * 100) : 0;

  const handleChallenge = async () => {
    setConfirmOpen(false);
    try {
      await challengeAPI.create(player.player_id);
      toast.success('Challenge sent!');
      fetchPlayer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to challenge');
    }
  };

  return (
    <div className="space-y-6" data-testid="player-profile-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-[#6a766e] hover:text-[#013523] transition-colors"
        data-testid="player-back-btn"
      >
        <ArrowLeft size={20} />
        <span className="text-sm">Back</span>
      </button>

      {isMe && editing ? (
        <OwnProfileEditor
          user={user}
          player={player}
          onCancel={() => setEditing(false)}
          onSaved={async () => {
            await refreshUser();
            await fetchPlayer();
            setEditing(false);
          }}
        />
      ) : (
        <>
          {/* Player header */}
          <div className="text-center relative">
            {isMe && (
              <button
                onClick={() => setEditing(true)}
                data-testid="edit-profile-button"
                className="absolute top-0 right-0 flex items-center gap-1 text-xs text-[#6a766e] hover:text-[#015e3f] bg-[#f0ece3] border border-[#e3dcd1] rounded-full px-3 py-1.5 transition-colors"
              >
                <PencilSimple size={14} /> Edit
              </button>
            )}
            <div className="flex justify-center">
              <Avatar
                src={player.avatar_url}
                name={player.full_name || player.name}
                size="xl"
                testId="player-avatar"
              />
            </div>
            <h1 className="font-serif text-3xl text-[#013523] mt-3" data-testid="player-name">{player.full_name || player.name}</h1>
            <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-sm px-3 py-1 rounded-full ${player.status === 'active' ? 'bg-[#015e3f]/10 text-[#015e3f]' : 'bg-[#6a766e]/10 text-[#6a766e]'}`} data-testid="player-status">
                {player.status === 'active' ? 'Active' : (<><Pause size={12} weight="fill" />Paused</>)}
              </span>
              {player.status === 'active' && player.rank > 0 && (
                <span className="text-sm text-[#c2a251] font-serif font-semibold" data-testid="player-rank">Rank #{player.rank}</span>
              )}
              <RankMovement movement={player.movement} testId="player-movement" />
              {player.usta_rating && (
                <span data-testid="player-usta-rating" className="inline-flex items-center gap-1 text-[11px] tracking-wider uppercase bg-[#f0ece3] text-[#013523] px-2.5 py-1 rounded-full border border-[#e3dcd1]">
                  USTA {player.usta_rating}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <StatCard icon={Trophy} label="Win Rate" value={`${winRate}%`} />
            <StatCard icon={TrendUp} label="Record" value={`${player.wins}-${player.losses}`} />
            <StatCard icon={CalendarBlank} label="Matches" value={totalMatches} />
          </div>

          <ContactCard player={player} isSelf={isMe} />

          {canChallenge && (
            <button
              data-testid="challenge-player-button"
              onClick={() => setConfirmOpen(true)}
              className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3.5 font-medium flex items-center justify-center gap-2 transition-transform active:scale-95 hover:bg-[#014d34]"
            >
              <Lightning size={20} weight="fill" />
              Challenge {player.full_name || player.name}
            </button>
          )}

          {challenges && challenges.length > 0 && (
            <div>
              <h2 className="font-serif text-xl text-[#013523] mb-3">Active Challenges</h2>
              <div className="space-y-2">
                {challenges.map((ch) => (
                  <div key={ch.challenge_id} className="bg-[#f0ece3] rounded-2xl border border-[#e3dcd1] px-4 py-3">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-[#2a332c]">
                        <MemberLink profileId={ch.challenger_id} name={ch.challenger_name} className="font-medium" />
                        <span className="text-[#6a766e]"> vs </span>
                        <MemberLink profileId={ch.challenged_id} name={ch.challenged_name} className="font-medium" />
                      </div>
                      <StatusPill
                        status={ch.status}
                        matchState={ch.match_state}
                        testId={`profile-challenge-status-${ch.challenge_id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-serif text-xl text-[#013523] mb-3">Match History</h2>
            {matches.length === 0 ? (
              <p className="text-sm text-[#6a766e]" data-testid="no-matches-text">No matches yet</p>
            ) : (
              <div className="space-y-2" data-testid="match-history-list">
                {matches.map((m) => (
                  <MatchHistoryRow key={m.match_id} match={m} viewerId={player.player_id} />
                ))}
              </div>
            )}
          </div>

          {/* Own-profile Sign Out — same canonical logout as Settings.
              Every authenticated user (regular, EM, admin) can sign out
              from their own profile without needing the bottom-nav
              Settings tab (which is hidden for admins). */}
          {isMe && (
            <button
              data-testid="profile-logout-button"
              onClick={logout}
              className="w-full bg-red-50 text-red-600 border border-red-200 rounded-full px-6 py-3 font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform min-h-[44px]"
            >
              <SignOut size={20} />
              Sign Out
            </button>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmOpen && !!player}
        title="Send challenge?"
        message={player ? `Are you sure you want to challenge ${player.full_name || player.name}?` : ''}
        confirmLabel="Challenge"
        onConfirm={handleChallenge}
        onCancel={() => setConfirmOpen(false)}
        testId="confirm-challenge-dialog"
      />

    </div>
  );
}

function OwnProfileEditor({ user, player, onCancel, onSaved }) {
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  // First/last/email/opt-ins live on PROFILE; phone + usta_rating live on player (and mirrored on profile)
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(player?.phone || user?.phone || '');
  const [ustaRating, setUstaRating] = useState(player?.usta_rating || user?.usta_rating || '');
  const [emailOptIn, setEmailOptIn] = useState(user?.email_opt_in ?? true);
  const [smsOptIn, setSmsOptIn] = useState(user?.sms_opt_in ?? false);
  const [showPhone, setShowPhone] = useState(user?.show_phone_to_members ?? true);
  const [showEmail, setShowEmail] = useState(user?.show_email_to_members ?? true);

  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('Image must be under 3 MB'); return; }
    setAvatarBusy(true);
    try {
      await playerAPI.uploadAvatar(file);
      toast.success('Profile photo updated');
      await onSaved?.({ skipClose: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setAvatarBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) { toast.error('First and last name are required'); return; }
    // Email is read-only during beta — no longer validated or submitted from
    // this form. Admin SQL is the only path until the dedicated admin
    // change-email endpoint ships.
    setSaving(true);
    try {
      // Profile fields only — email is intentionally not sent.
      await playerAPI.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
        usta_rating: ustaRating || null,
        email_opt_in: emailOptIn,
        sms_opt_in: smsOptIn,
        show_phone_to_members: showPhone,
        show_email_to_members: showEmail,
      });
      toast.success('Profile updated');
      await onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="profile-edit-form">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl text-[#013523]">Edit Profile</h1>
        <button onClick={onCancel} data-testid="edit-profile-cancel" className="text-[#6a766e] hover:text-[#013523] p-1">
          <X size={22} />
        </button>
      </div>

      {/* Avatar */}
      <div className="flex items-center gap-4 bg-[#f0ece3] rounded-2xl border border-[#e3dcd1] p-4">
        <div className="relative">
          <Avatar src={user?.avatar_url} name={`${firstName} ${lastName}`.trim() || user?.email} size="lg" testId="edit-avatar" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={avatarBusy}
            data-testid="edit-upload-avatar-button"
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#015e3f] text-white flex items-center justify-center shadow-md border-2 border-[#f0ece3] active:scale-95 disabled:opacity-60"
            title="Change photo"
          >
            {avatarBusy ? <span className="block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Camera size={14} weight="fill" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onAvatarPick} className="hidden" data-testid="edit-avatar-file-input" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium text-[#013523]">Profile photo</div>
          <div className="text-xs text-[#6a766e]">Max 3 MB · JPG, PNG, or GIF</div>
        </div>
      </div>

      {/* Name */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#6a766e] mb-1.5 block">First Name</label>
          <input data-testid="edit-first-name" type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
            className="w-full bg-white border border-[#e3dcd1] rounded-xl px-3 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#6a766e] mb-1.5 block">Last Name</label>
          <input data-testid="edit-last-name" type="text" value={lastName} onChange={e => setLastName(e.target.value)}
            className="w-full bg-white border border-[#e3dcd1] rounded-xl px-3 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]" />
        </div>
      </div>

      {/* Email — temporarily read-only during beta. Editing email previously
          updated auth.users + profiles + players via PUT /players/email,
          but a small number of users created split identities by changing
          email out-of-band, so we've locked self-service edits until an
          admin "Change email" endpoint exists. See lockdown rationale. */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#6a766e] mb-1.5 block">Email</label>
        <input
          data-testid="edit-email"
          type="email"
          value={email}
          readOnly
          disabled
          aria-disabled="true"
          className="w-full bg-[#f0ece3] border border-[#e3dcd1] rounded-xl px-3 py-2.5 text-sm text-[#6a766e] cursor-not-allowed focus:outline-none"
        />
        <p
          className="text-[11px] text-[#6a766e] mt-1"
          data-testid="edit-email-locked-helper"
        >
          Email changes currently require admin support.
        </p>
      </div>

      {/* Phone */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#6a766e] mb-1.5 block">Phone</label>
        <input data-testid="edit-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567"
          className="w-full bg-white border border-[#e3dcd1] rounded-xl px-3 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]" />
      </div>

      {/* USTA */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#6a766e] mb-1.5 block">USTA Rating</label>
        <select data-testid="edit-usta" value={ustaRating} onChange={e => setUstaRating(e.target.value)}
          className="w-full bg-white border border-[#e3dcd1] rounded-xl px-3 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] appearance-none">
          <option value="">Select…</option>
          {USTA_RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Opt-ins */}
      <div className="space-y-3 bg-[#f0ece3] rounded-2xl border border-[#e3dcd1] p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="edit-email-opt-in" type="checkbox" checked={emailOptIn} onChange={e => setEmailOptIn(e.target.checked)}
            className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f]" />
          <span className="text-sm text-[#2a332c]">Email notifications</span>
        </label>
        <label className={`flex items-center gap-3 ${user?.sms_suppressed ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
          <input data-testid="edit-sms-opt-in" type="checkbox"
            checked={user?.sms_suppressed ? false : smsOptIn}
            disabled={!!user?.sms_suppressed}
            onChange={e => setSmsOptIn(e.target.checked)}
            className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f]" />
          <span className="text-sm text-[#2a332c]">SMS notifications</span>
        </label>
        {user?.sms_suppressed && (
          <div data-testid="sms-opted-out-note" className="ml-7 -mt-1 text-xs text-[#b4532a]">
            SMS: Opted Out — you replied STOP. Text <span className="font-semibold">START</span> to the Rippers number to turn SMS back on.
          </div>
        )}
      </div>

      {/* Contact privacy (Phase C1.5) */}
      <div className="space-y-3 bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-4">
        <div>
          <div className="text-[13px] font-medium text-[#013523]">Contact privacy</div>
          <div className="text-[11.5px] text-[#6a766e] mt-0.5">
            Choose which contact details other signed-in Rippers Club members can see.
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="edit-show-phone" type="checkbox" checked={showPhone} onChange={e => setShowPhone(e.target.checked)}
            className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f]" />
          <span className="text-sm text-[#2a332c]">Show my phone number to club members</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="edit-show-email" type="checkbox" checked={showEmail} onChange={e => setShowEmail(e.target.checked)}
            className="w-4 h-4 rounded border-[#e3dcd1] text-[#015e3f]" />
          <span className="text-sm text-[#2a332c]">Show my email address to club members</span>
        </label>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 sticky bottom-0 bg-[#fbfaf8] pt-2">
        <button data-testid="edit-save-button" onClick={handleSave} disabled={saving}
          className="flex-1 bg-[#015e3f] text-white rounded-full px-4 py-3 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50">
          <Check size={16} weight="bold" /> {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel}
          className="bg-white text-[#013523] border border-[#e3dcd1] rounded-full px-5 py-3 text-sm font-medium active:scale-95">
          Cancel
        </button>
      </div>
    </div>
  );
}

function MatchHistoryRow({ match, viewerId }) {
  const won = match.winner_id === viewerId;
  const opponentName = match.player1_id === viewerId ? match.player2_name : match.player1_name;
  const dateStr = match.created_at ? new Date(match.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  return (
    <div className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] px-4 py-3" data-testid={`match-row-${match.match_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${won ? 'bg-[#015e3f] text-white' : 'bg-[#6a766e]/20 text-[#6a766e]'}`}>
              {won ? 'W' : 'L'}
            </span>
            <span className="text-sm font-medium text-[#2a332c] truncate">
              <MemberLink profileId={match.player1_id} name={match.player1_name} /> <span className="text-[#6a766e] font-normal">vs</span> <MemberLink profileId={match.player2_id} name={match.player2_name} />
            </span>
          </div>
          <div className="text-xs text-[#6a766e] mt-1">
            {won ? 'Defeated' : 'Lost to'} <MemberLink profileId={match.player1_id === viewerId ? match.player2_id : match.player1_id} name={opponentName} className="text-[#2a332c]" />
            {dateStr && <span> · {dateStr}</span>}
          </div>
        </div>
        <div className="text-right">
          {isWalkover(match.score) ? (
            <div className="inline-block text-[10px] font-semibold tracking-[0.1em] uppercase text-[#c2a251] bg-[#c2a251]/10 border border-[#c2a251]/30 rounded-full px-2 py-0.5" data-testid="match-walkover-label">
              Walkover
            </div>
          ) : (
            <div className="text-xs font-mono text-[#013523] font-medium">{formatScore(match.score)}</div>
          )}
          <div className="text-[10px] text-[#6a766e] mt-0.5">Winner: {match.winner_name || 'N/A'}</div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {  return (
    <div className="bg-[#f0ece3] rounded-2xl border border-[#e3dcd1] p-4 text-center">
      <Icon size={22} weight="duotone" className="text-[#015e3f] mx-auto mb-2" />
      <div className="font-serif text-2xl text-[#013523] font-semibold">{value}</div>
      <div className="text-[10px] tracking-[0.1em] uppercase text-[#6a766e] mt-1">{label}</div>
    </div>
  );
}

// Phase C1.5b — Polished Contact section for the canonical PlayerPage.
// Self always sees own values with a "Visible / Hidden" pill; other members
// see only what the owner opted to share; both-hidden falls back to a
// subtle message so the card never looks broken.
function ContactCard({ player: p, isSelf }) {
  const havePhone = !!p.phone;
  const haveEmail = !!p.email;
  const showPhone = p.show_phone_to_members !== false;
  const showEmail = p.show_email_to_members !== false;

  if (!havePhone && !haveEmail && !isSelf) {
    return (
      <div className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 text-center"
           data-testid="profile-contact-hidden">
        <p className="text-[12.5px] italic text-[#94988f]">Contact details hidden by member</p>
      </div>
    );
  }

  if (!havePhone && !haveEmail && isSelf) return null;

  return (
    <div className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3" data-testid="profile-contact">
      <div className="grid grid-cols-3 gap-2">
        <ContactBtn
          testid="profile-call-btn"
          disabled={!havePhone} href={havePhone ? `tel:${p.phone}` : null}
          icon={<Phone size={16} weight="bold" />} label="Call" />
        <ContactBtn
          testid="profile-text-btn"
          disabled={!havePhone} href={havePhone ? `sms:${p.phone}` : null}
          icon={<ChatCircleText size={16} weight="bold" />} label="Text" />
        <ContactBtn
          testid="profile-email-btn"
          disabled={!haveEmail} href={haveEmail ? `mailto:${p.email}` : null}
          icon={<EnvelopeSimple size={16} weight="bold" />} label="Email" />
      </div>
      <div className="space-y-2">
        {havePhone && (
          <div className="flex items-center justify-between gap-3" data-testid="profile-phone-row">
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-widest text-[#6a766e] font-medium">Phone</div>
              <a href={`tel:${p.phone}`} className="text-[15px] text-[#013523] hover:text-[#015e3f] block truncate tabular-nums">{p.phone}</a>
            </div>
            {isSelf && <VisibilityPill visible={showPhone} testid="profile-phone-visibility" />}
          </div>
        )}
        {haveEmail && (
          <div className="flex items-center justify-between gap-3" data-testid="profile-email-row">
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-widest text-[#6a766e] font-medium">Email</div>
              <a href={`mailto:${p.email}`} className="text-[15px] text-[#013523] hover:text-[#015e3f] block truncate">{p.email}</a>
            </div>
            {isSelf && <VisibilityPill visible={showEmail} testid="profile-email-visibility" />}
          </div>
        )}
      </div>
    </div>
  );
}

function VisibilityPill({ visible, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-medium whitespace-nowrap ${
      visible
        ? 'bg-[#e6f4ea] border-[#b7e0c1] text-[#015e3f]'
        : 'bg-[#f0ece3] border-[#e3dcd1] text-[#6a766e]'
    }`}>
      {visible ? 'Visible to club members' : 'Hidden from club members'}
    </span>
  );
}

function ContactBtn({ testid, href, icon, label, disabled }) {
  const cls = 'flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[12.5px] font-medium transition';
  if (disabled || !href) {
    return (
      <div data-testid={testid} aria-disabled className={`${cls} bg-[#f0ece3] border-[#e3dcd1] text-[#94988f] cursor-not-allowed`}>
        {icon} {label}
      </div>
    );
  }
  return (
    <a href={href} data-testid={testid} className={`${cls} bg-white border-[#e3dcd1] text-[#013523] hover:border-[#015e3f] hover:bg-[#f0f7f2]`}>
      {icon} {label}
    </a>
  );
}
